/**
 * Known public URLs for SHX and TTF fonts served with CORS allowed.
 *
 * `raw.githubusercontent.com` serves static binary blobs with the
 * `access-control-allow-origin: *` header, which makes it the only
 * source we can rely on for cross-origin `fetch()` of font bytes from
 * the browser without a server proxy. Other sources (Autodesk hosted
 * CDNs, third-party mirrors) require per-source CORS vetting before
 * we can add them.
 *
 * ### Keys
 *
 * Keys are normalised the same way `FontRegistry` and `FontStore` do
 * (lowercase, trimmed, `.shx/.ttf/.otf` stripped) so a lookup by
 * either `"SIMPLEX.shx"`, `" simplex "`, or `"Simplex"` hits the same
 * entry — matching the case-insensitive, suffix-agnostic behaviour of
 * AutoCAD style `fontName` resolution.
 *
 * ### Scope
 *
 * The whitelist is intentionally minimal. Both entries were verified
 * 2026-04 to return HTTP 200 with `access-control-allow-origin: *`.
 * Expanding the list requires verifying CORS for each new source —
 * do not add entries speculatively.
 *
 * ### GOST fonts
 *
 * The Russian GOST-series fonts (gost_a, gost_b, gost_aui, …) are NOT
 * available in public raw form on GitHub as of 2026-04. Users with
 * GOST requirements must upload the SHX manually via the dropzone in
 * `FontManagerDialog`.
 */

/**
 * Shape of a whitelist entry. `kind` matches the `RegisteredFont`
 * discriminator so downstream code can avoid re-detecting format when
 * it already knows it from the source.
 *
 * `filename` is preserved for future use (diagnostics, downloaded-file
 * name hints); it is not consumed by `webFontFetcher` today but keeps
 * the shape forward-compatible.
 */
export type FontWhitelistEntry = {
  url: string;
  kind: "shx" | "ttf";
  filename: string;
};

/**
 * Canonical whitelist. Keys are normalised lookup names — callers use
 * `lookupWhitelistUrl` / `isInWhitelist` rather than indexing directly
 * so the normalisation rule stays in one place.
 */
export const FONT_WHITELIST: Record<string, FontWhitelistEntry> = {
  simplex: {
    url: "https://raw.githubusercontent.com/thingraph/dwg-viewer-example/main/libs/fonts/simplex.shx",
    kind: "shx",
    filename: "simplex.shx",
  },
  hztxt: {
    url: "https://raw.githubusercontent.com/thingraph/dwg-viewer-example/main/libs/fonts/hztxt.shx",
    kind: "shx",
    filename: "hztxt.shx",
  },
  // NOTE: GOST fonts are not available in public raw form on GitHub as of
  // 2026-04. Users with GOST requirements must upload manually via the
  // dropzone in FontManagerDialog.
};

/**
 * Normalise a DXF STYLE `fontName` reference to the whitelist key
 * format: lowercase, trimmed, with any `.shx` / `.ttf` / `.otf`
 * extension stripped. Duplicates the `normalizeFontKey` logic here so
 * whitelist.ts stays standalone (no cross-module import) and can be
 * consumed by the webFontFetcher without pulling the fontStore.
 */
function normalize(fontName: string): string {
  return fontName
    .trim()
    .toLowerCase()
    .replace(/\.(shx|ttf|otf)$/u, "");
}

/**
 * Look up the public URL for a font by its DXF reference name.
 * Returns `undefined` if the name is not known in the whitelist.
 */
export function lookupWhitelistUrl(fontName: string): string | undefined {
  const entry = FONT_WHITELIST[normalize(fontName)];
  return entry?.url;
}

/**
 * Predicate: is this font name covered by the public whitelist? Used
 * by `FontManagerDialog` to disable the "fetch" button (with a tooltip
 * explaining why) for fonts that would never succeed.
 */
export function isInWhitelist(fontName: string): boolean {
  return FONT_WHITELIST[normalize(fontName)] !== undefined;
}
